import React, { useEffect, useState } from 'react';
import { Mail, MessageCircle, Clock, CheckCircle2, AlertTriangle, Eye, Loader2 } from 'lucide-react';
import { adminApi } from './api';
import Curve from './Curve';

const PERIODS = [
  { value: 7,  label: '7 дней' },
  { value: 30, label: '30 дней' },
  { value: 90, label: '90 дней' },
];

function NumberTile({ icon: Icon, label, value, unit, sublabel, curve, color = 'gold', testid }) {
  return (
    <div
      className="number-tile"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="dark-eyebrow">{label}</div>
        <Icon size={16} style={{ color: color === 'wine' ? 'var(--wine-bright)' : 'var(--gold)' }} strokeWidth={1.6} />
      </div>
      <div className="number-tile-value">
        <span className={`number-tile-big ${color === 'wine' ? 'wine' : ''}`}>{value}</span>
        {unit && <span className="number-tile-unit">{unit}</span>}
      </div>
      {sublabel && <div className="dark-meta">{sublabel}</div>}
      {curve && (
        <div style={{ marginTop: 'auto', paddingTop: 6 }}>
          <Curve values={curve} color={color} height={36} />
        </div>
      )}
    </div>
  );
}

export default function AdminNumbers() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let stopped = false;
    setLoading(true);
    const fetchIt = async () => {
      try {
        const d = await adminApi.metrics(days);
        if (!stopped) { setData(d); setError(''); }
      } catch (err) {
        if (!stopped) setError(err.message || 'Не получилось загрузить');
      } finally {
        if (!stopped) setLoading(false);
      }
    };
    fetchIt();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', padding: '120px 0' }} data-testid="numbers-loading">
        <Loader2 size={28} className="animate-spin" style={{ color: 'var(--cream-soft)' }} />
      </div>
    );
  }

  const d = data || {};
  const totals = d.totals || {};
  const topics = d.top_topics || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} data-testid="admin-numbers">
      {/* Шапка раздела */}
      <div className="dark-card" style={{ padding: 28 }}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <div style={{ flex: 1 }}>
            <div className="dark-eyebrow">Замеры и кривые</div>
            <h1 className="dark-h1" style={{ fontSize: 42, marginTop: 6 }}>Цифры</h1>
            <p className="dark-meta" style={{ marginTop: 8 }}>
              Сколько посетителей, сколько заявок, сколько ответили — за выбранный отрезок.
            </p>
          </div>
          <div className="flex gap-2" data-testid="period-switch">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setDays(p.value)}
                className={`dark-btn ${days === p.value ? 'dark-btn-gold' : 'dark-btn-ghost'}`}
                style={{ padding: '8px 14px', fontSize: 13 }}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="dark-card" style={{ padding: 18, color: 'var(--wine-bright)' }}>{error}</div>
      )}

      {/* Плитка показателей */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14,
      }}>
        <NumberTile
          icon={Mail}
          label="Заявки"
          value={totals.contacts || 0}
          unit={`за ${days} дн.`}
          curve={d.series_contacts}
          color="gold"
          testid="tile-contacts"
        />
        <NumberTile
          icon={MessageCircle}
          label="Разговоры с Аней"
          value={totals.chats || 0}
          unit={`за ${days} дн.`}
          curve={d.series_chats}
          color="wine"
          testid="tile-chats"
        />
        <NumberTile
          icon={Clock}
          label="В среднем длится"
          value={d.avg_chat_minutes || 0}
          unit="мин"
          sublabel="один разговор с Аней"
          color="gold"
          testid="tile-avg"
        />
        <NumberTile
          icon={CheckCircle2}
          label="Доля ответов"
          value={d.response_rate_pct || 0}
          unit="%"
          sublabel={`${totals.answered || 0} ответов из ${totals.contacts || 0}`}
          color="gold"
          testid="tile-response"
        />
      </div>

      {/* Кризисы */}
      {totals.crisis > 0 && (
        <div className="dark-card wine" style={{ padding: 22 }} data-testid="crisis-summary">
          <div className="flex items-center gap-3">
            <AlertTriangle size={18} style={{ color: 'var(--wine-bright)' }} />
            <div>
              <div className="dark-h2" style={{ fontSize: 20 }}>
                Кризисных запросов за период: {totals.crisis}
              </div>
              <div className="dark-meta" style={{ marginTop: 4 }}>
                Загляните на «Главную» — там полный текст каждого
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Частые темы */}
      <div className="dark-card" style={{ padding: 24 }} data-testid="topics-card">
        <div className="dark-eyebrow" style={{ marginBottom: 12 }}>Частые темы у Ани и в заявках</div>
        {topics.length === 0 ? (
          <p className="dark-meta">Пока недостаточно разговоров, чтобы выделить темы.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {topics.map((t) => (
              <span key={t.word} className="dark-tag" data-testid={`topic-${t.word}`}>
                {t.word} · {t.count}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Просмотры сайта — placeholder */}
      <div className="dark-card" style={{ padding: 24, opacity: 0.65 }} data-testid="views-placeholder">
        <div className="flex items-start gap-3">
          <Eye size={18} style={{ color: 'var(--cream-soft)', marginTop: 4 }} />
          <div>
            <div className="dark-h2" style={{ fontSize: 20 }}>Просмотры сайта</div>
            <p className="dark-meta" style={{ marginTop: 6, lineHeight: 1.6 }}>
              Чтобы видеть, сколько людей зашло на сайт за день — подключите Яндекс.Метрику или Plausible.
              Это делается в разделе «Настройки» → «Аналитика». Без них никакие данные о посетителях не собираются.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
